import React, { useState } from 'react';
import useDialog from '../hooks/useDialog';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useNotifications } from '../contexts/NotificationContext';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBell, faDownload, faTrash, faExclamationTriangle, faFileContract, faShieldAlt, faBuilding, faCookieBite, faEnvelope, faAdjust, faBookOpen, faPenNib, faCircleQuestion, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { deleteAccount, exportData } from '../services/api';
import { reopenCookieSettings, PRIVACY_SETTINGS_KEY } from './CookieConsent';
import { useTranslation } from '../contexts/LanguageContext';
import { LanguageGrid } from './LanguageSelector';
import '../css/auth.css';

// Get CSRF token from cookie
function getCSRFToken() {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

function ToggleRow({ icon, label, checked, onChange }) {
    return (
        <button type="button" className="settings-toggle-row" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}>
            <span className="settings-toggle-icon" aria-hidden="true">{icon}</span>
            <span className="settings-toggle-label">{label}</span>
            <span className={`settings-switch${checked ? ' on' : ''}`} aria-hidden="true"><span className="settings-switch-knob" /></span>
        </button>
    );
}

const SettingsModal = ({ show, onClose }) => {
    const dialogRef = useDialog(show, onClose);
    const { t } = useTranslation();
    const { user, isAuthenticated, checkAuth } = useAuth();
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const { preferences, updatePreference } = usePreferences();
    const { pushEnabled, pushSubscribed, pushSupported, requestPush, togglePush } = useNotifications();
    useBodyScrollLock(show);
    const [username, setUsername] = useState(user?.username || '');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    if (!show) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!username.trim()) {
            setError(t('auth.errors.usernameEmpty'));
            return;
        }

        if (username.length < 3) {
            setError(t('auth.errors.usernameTooShort'));
            return;
        }

        if (username === user?.username) {
            setError(t('settings.usernameSame'));
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/auth/update-profile/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFToken(),
                },
                credentials: 'include',
                body: JSON.stringify({ username: username.trim() }),
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(t('settings.usernameUpdated'));
                await checkAuth();
                setTimeout(() => {
                    onClose();
                }, 1000);
            } else {
                setError(data.error || t('settings.usernameUpdateFailed'));
            }
        } catch (error) {
            console.error('Update profile error:', error);
            setError(error.message || t('settings.profileUpdateFailed'));
        } finally {
            setLoading(false);
        }
    };

    const contactSupport = () => {
        const email = 'support@word-labyrinth.com';
        if (window.Capacitor?.isNativePlatform?.()) {
            navigator.clipboard?.writeText?.(email)
                .then(() => alert(t('nav.emailCopiedAlert', { email })))
                .catch(() => alert(t('nav.contactUsAt', { email })));
        } else {
            window.location.href = `mailto:${email}`;
        }
    };

    return (
        <div className="modal-overlay-base modal-overlay" onClick={onClose}>
            <div className="modal-content-base login-modal settings-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={onClose} aria-label={t('common.close')}>
                    <span aria-hidden="true">&times;</span>
                </button>

                <div className="modal-header">
                    <h2>{t('settings.title')}</h2>
                </div>

                {error && (
                    <div className="error-message">
                        <i className="fa fa-exclamation-circle"></i>
                        {error}
                    </div>
                )}

                {success && (
                    <div className="error-message" style={{
                        background: '#f0fdf4',
                        borderColor: '#bbf7d0',
                        color: '#16a34a'
                    }}>
                        <i className="fa fa-check-circle"></i>
                        {success}
                    </div>
                )}

                <div className="settings-section">
                    <h3 className="settings-section-title">{t('settings.appearance')}</h3>
                    <ToggleRow icon={isDarkMode ? '🌙' : '☀️'} label={t('nav.darkMode')} checked={isDarkMode} onChange={toggleDarkMode} />
                    <ToggleRow
                        icon={<FontAwesomeIcon icon={faAdjust} />}
                        label={t('settings.highContrast')}
                        checked={!!preferences.highContrast}
                        onChange={value => updatePreference('highContrast', value)}
                    />
                </div>

                <div className="settings-divider" />

                <div className="settings-section">
                    <h3 className="settings-section-title">{t('nav.language')}</h3>
                    <LanguageGrid compact />
                </div>

                {pushSupported && (
                    <>
                        <div className="settings-divider" />
                        <div className="settings-section">
                            <h3 className="settings-section-title">
                                <FontAwesomeIcon icon={faBell} style={{ marginRight: '6px' }} />
                                {t('settings.dailyReminders')}
                            </h3>
                            {/* The toggle only works on an existing subscription; without one, ask again. */}
                            {!pushSubscribed ? (
                                <button type="button" className="submit-button" onClick={requestPush}>
                                    {t('settings.enableNotifications')}
                                </button>
                            ) : (
                                <ToggleRow
                                    icon={<FontAwesomeIcon icon={faBell} />}
                                    label={pushEnabled ? t('settings.notificationsOn') : t('settings.notificationsOff')}
                                    checked={pushEnabled}
                                    onChange={togglePush}
                                />
                            )}
                        </div>
                    </>
                )}

                {isAuthenticated && (
                    <>
                        <div className="settings-divider" />
                        <div className="settings-section">
                            <h3 className="settings-section-title">{t('settings.account')}</h3>
                            <form onSubmit={handleSubmit} className="auth-form">
                                <div className="form-group">
                                    <label htmlFor="username">{t('auth.username')}</label>
                                    <input
                                        type="text"
                                        id="username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder={t('auth.usernamePlaceholder')}
                                        minLength={3}
                                        maxLength={30}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('auth.email')}</label>
                                    <input
                                        type="email"
                                        value={user?.email || ''}
                                        disabled
                                        style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="submit-button"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    ) : (
                                        t('settings.saveChanges')
                                    )}
                                </button>
                            </form>
                        </div>

                        <div className="settings-divider" />

                        <div className="settings-section">
                            <h3 className="settings-section-title">{t('settings.dataPrivacy')}</h3>

                            <button
                                type="button"
                                className="submit-button"
                                style={{ background: '#f0f0f0', color: '#333', marginBottom: '8px' }}
                                disabled={exportLoading}
                                onClick={async () => {
                                    setExportLoading(true);
                                    setError('');
                                    try {
                                        const data = await exportData();
                                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'word-labyrinth-data-export.json';
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    } catch (err) {
                                        setError(t('settings.exportFailed'));
                                    } finally {
                                        setExportLoading(false);
                                    }
                                }}
                            >
                                {exportLoading ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    <><FontAwesomeIcon icon={faDownload} style={{ marginRight: '6px' }} /> {t('settings.exportData')}</>
                                )}
                            </button>

                            {!showDeleteConfirm ? (
                                <button
                                    type="button"
                                    className="submit-button"
                                    style={{ background: '#fee', color: '#c33', border: '1px solid #fcc' }}
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} /> {t('settings.deleteAccount')}
                                </button>
                            ) : (
                                <div style={{ background: '#fff5f5', border: '1px solid #fcc', borderRadius: '8px', padding: '16px', marginTop: '4px' }}>
                                    <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#c33' }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
                                        {t('settings.deleteWarning')}
                                    </p>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            type="button"
                                            className="submit-button"
                                            style={{ background: '#f0f0f0', color: '#333', flex: 1 }}
                                            onClick={() => setShowDeleteConfirm(false)}
                                            disabled={deleteLoading}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            className="submit-button"
                                            style={{ background: '#c33', color: '#fff', flex: 1 }}
                                            disabled={deleteLoading}
                                            onClick={async () => {
                                                setDeleteLoading(true);
                                                try {
                                                    const result = await deleteAccount();
                                                    if (result.success) {
                                                        window.location.href = '/';
                                                    } else {
                                                        setError(result.error || t('settings.deleteFailed'));
                                                    }
                                                } catch (err) {
                                                    setError(t('settings.deleteFailedContact', { email: 'support@word-labyrinth.com' }));
                                                } finally {
                                                    setDeleteLoading(false);
                                                }
                                            }}
                                        >
                                            {deleteLoading ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : (
                                                t('settings.confirmDelete')
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="settings-divider" />

                <div className="settings-section settings-legal">
                    <h3 className="settings-section-title">{t('settings.learnMore')}</h3>
                    <div className="settings-legal-links">
                        <a href="/word-ladder/" className="settings-legal-link">
                            <FontAwesomeIcon icon={faBookOpen} /> {t('nav.whatIsWordLadder')}
                        </a>
                        <a href="/blog/" className="settings-legal-link">
                            <FontAwesomeIcon icon={faPenNib} /> {t('nav.blog')}
                        </a>
                        <a href="/faq/" className="settings-legal-link">
                            <FontAwesomeIcon icon={faCircleQuestion} /> {t('nav.faq')}
                        </a>
                        <a href="/about/" className="settings-legal-link">
                            <FontAwesomeIcon icon={faCircleInfo} /> {t('nav.about')}
                        </a>
                    </div>
                </div>

                <div className="settings-divider" />

                <div className="settings-section settings-legal">
                    <h3 className="settings-section-title">{t('settings.legalInfo')}</h3>
                    <div className="settings-legal-links">
                        <Link to="/terms" onClick={onClose} className="settings-legal-link">
                            <FontAwesomeIcon icon={faFileContract} /> {t('nav.terms')}
                        </Link>
                        <Link to="/privacy" onClick={onClose} className="settings-legal-link">
                            <FontAwesomeIcon icon={faShieldAlt} /> {t('nav.privacy')}
                        </Link>
                        <Link to="/impressum" onClick={onClose} className="settings-legal-link">
                            <FontAwesomeIcon icon={faBuilding} /> {t('nav.legalNotice')}
                        </Link>
                        <button
                            type="button"
                            className="settings-legal-link"
                            onClick={() => { reopenCookieSettings(); onClose(); }}
                        >
                            <FontAwesomeIcon icon={faCookieBite} /> {t(PRIVACY_SETTINGS_KEY)}
                        </button>
                        <button type="button" className="settings-legal-link" onClick={contactSupport}>
                            <FontAwesomeIcon icon={faEnvelope} /> {t('nav.contact')}
                        </button>
                    </div>
                    <div className="settings-copyright">
                        © {new Date().getFullYear()} CoreDL d.o.o.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;
